import type { AxiosInstance } from "axios";

import type { CampaignStatus, GoogleCampaignType, ReportDefinition } from "./reports.js";

/**
 * The async-export client: request a file, watch it, fetch a link.
 *
 * WHY DOWNLOADS NEVER USE A STORED URL. A GCS signature lasts an hour; a row on the My
 * Exports screen can be a week old. So the client asks `/export/download/:jobId` at
 * CLICK TIME and the API signs a fresh URL — link expiry stops being a thing a merchant
 * can experience. The endpoint returns JSON rather than redirecting because a browser
 * navigation cannot carry the `X-Tenant-Id` header this client sends.
 */

export type ExportFormat = "xlsx" | "csv";
export type ExportStatus = "queued" | "processing" | "ready" | "failed";

export interface ExportJobSummary {
  readonly id: string;
  readonly reportType: string;
  readonly status: ExportStatus;
  readonly progress: number;
  readonly rowCount: number | null;
  /** Set on failures — and on a READY row that was truncated ("Truncated at N rows"). */
  readonly error: string | null;
  /** What the file contains: format, from/to, and the filters the worker applied. */
  readonly filters: Readonly<Record<string, unknown>>;
  readonly requestedBy: string | null;
  readonly createdAt: string;
  readonly expiresAt: string | null;
  readonly urlExpired?: boolean;
}

export interface CreateExportRequest {
  readonly reportType: string;
  readonly from: string;
  readonly to: string;
  readonly format: ExportFormat;
  readonly filters: Readonly<Record<string, unknown>>;
  readonly columns: readonly string[];
}

interface Envelope<T> {
  readonly success: boolean;
  readonly message: string;
  readonly data: T;
}

export async function createExport(
  client: AxiosInstance,
  request: CreateExportRequest,
): Promise<{ id: string; status: string; progress: number }> {
  const response = await client.post<Envelope<{ id: string; status: string; progress: number }>>(
    "/api/v2/export/create",
    request,
  );
  return response.data.data;
}

export async function fetchExportStatus(client: AxiosInstance, jobId: string): Promise<ExportJobSummary> {
  const response = await client.get<Envelope<ExportJobSummary>>(`/api/v2/export/status/${jobId}`);
  return response.data.data;
}

export async function fetchExportList(
  client: AxiosInstance,
  paging: { readonly limit: number; readonly offset: number },
): Promise<readonly ExportJobSummary[]> {
  const response = await client.get<Envelope<{ items: readonly ExportJobSummary[] }>>("/api/v2/export/list", {
    params: paging,
  });
  return response.data.data.items;
}

export async function fetchDownloadUrl(client: AxiosInstance, jobId: string): Promise<string> {
  const response = await client.get<Envelope<{ url: string; expiresAt: string }>>(
    `/api/v2/export/download/${jobId}`,
  );
  return response.data.data.url;
}

/**
 * Hand the signed URL to the browser. An anchor click rather than `location.href` so the
 * page does not navigate — the SPA (and the export tray) stay exactly where they are.
 */
export function triggerBrowserDownload(url: string): void {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

/** What the ReportScreen knows at the moment the merchant clicks Export. */
export interface ExportFilterState {
  readonly sortBy: string | null;
  readonly direction: "asc" | "desc";
  readonly status: CampaignStatus;
  readonly campaignType: GoogleCampaignType | "all";
  readonly hierarchy: string;
  readonly metaCampaignId?: string;
  readonly metaAdsetId?: string;
  readonly googleCampaignId?: string;
  readonly googleAdgroupId?: string;
  /** The table's search box. Trimmed; blank means "not searching". */
  readonly search: string;
  readonly breakdown?: "week" | "day";
}

/**
 * The screen's controls, as the export API's `filters` object — the SAME assembly rules
 * `fetchReport` uses for the live table (defaults omitted, platform-specific fields only
 * on their own platform), so the file describes exactly the view on screen. Pure, so the
 * drill-down matrix is testable without rendering anything.
 */
export function buildExportFilters(
  report: ReportDefinition,
  state: ExportFilterState,
): Readonly<Record<string, unknown>> {
  const filters: Record<string, unknown> = {};

  if (state.breakdown !== undefined) {
    filters["breakdown"] = state.breakdown;
  }

  if (report.sortable && state.sortBy !== null) {
    filters["sortBy"] = state.sortBy;
    filters["direction"] = state.direction;
  }
  if (report.statusFilter === true && state.status !== "all") {
    filters["status"] = state.status;
  }
  if (report.googleHierarchy === true || report.metaHierarchy === true) {
    if (state.hierarchy !== "campaign") filters["hierarchy"] = state.hierarchy;
    if (report.googleHierarchy === true && state.campaignType !== "all") {
      filters["campaignType"] = state.campaignType;
    }
    if (report.metaHierarchy === true) {
      if (state.metaCampaignId !== undefined) filters["campaignId"] = state.metaCampaignId;
      if (state.metaAdsetId !== undefined) filters["adsetId"] = state.metaAdsetId;
    }
    if (report.googleHierarchy === true) {
      if (state.googleCampaignId !== undefined) filters["campaignId"] = state.googleCampaignId;
      if (state.googleAdgroupId !== undefined) filters["adgroupId"] = state.googleAdgroupId;
    }
  }

  const search = state.search.trim();
  if (search !== "") filters["search"] = search;

  return filters;
}
